/**
 * Where an OHT hangs from the rail.
 *
 * Lane-snap places the OHT footprint centre on the FAB (red) track. The 2D
 * graphic pins its gray rail stub to that same centre so gray sits on red,
 * with the body/FOUP hanging below.
 */
import { LayoutElement } from '../types';
import { getOht2DAttachLocal } from '../components/canvas/amhs/Oht2D/ohtGeometry';

type LegacyPivot = LayoutElement & { pivotX?: unknown; pivotY?: unknown };

function readOverride(element: LayoutElement, key: 'pivotX' | 'pivotY'): number | null {
  const fromCustom = element.customProperties?.[key];
  const raw = fromCustom !== undefined ? fromCustom : (element as LegacyPivot)[key];
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Rail-attachment point in the vehicle's local frame (= gray rail on red track).
 */
export function getOhtPivot(
  element: LayoutElement,
  w: number = element.dimensions.width,
  h: number = element.dimensions.height
): { pivotX: number; pivotY: number } {
  const attach = getOht2DAttachLocal(w, h, true);
  return {
    pivotX: readOverride(element, 'pivotX') ?? attach.x,
    pivotY: readOverride(element, 'pivotY') ?? attach.y,
  };
}

/** Pivot in world space for an element at its current position/rotation. */
export function getOhtPivotWorld(
  element: LayoutElement,
  w?: number,
  h?: number
): { x: number; y: number } {
  const width = w ?? element.dimensions.width;
  const height = h ?? element.dimensions.height;
  const { pivotX, pivotY } = getOhtPivot(element, width, height);
  const rad = ((element.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: element.position.x + pivotX * cos - pivotY * sin,
    y: element.position.y + pivotX * sin + pivotY * cos,
  };
}
